import React, { useMemo, useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import InsertChartIcon from "@mui/icons-material/InsertChart";
import { CustomIconButton } from "../CustomToggleButton";
import { RichTooltipPopover } from "../../RichTooltipPopover";
import PercentageGraph from "../stackedGraph/PercentageGraph";
import { isEmpty } from "lodash-es";
import { useGetTheme } from "../../../../hooks";
import { flattenObject, adjustPercentages } from "../utils";
import { LEGEND_MARKER_FOR_COACH_USAGE } from "../consts";

function findLowestAssignmentId(objects) {
  let lowestId = Infinity;
  for (const obj of objects) {
    if (obj.id < lowestId) {
      lowestId = obj.id;
    }
  }
  return lowestId;
}

const renderBarLabel = (d) => {
  const value = Math.round(d.total);
  return `${value}%`;
};

const ChatReport = ({ data, courseAssignments, tooltip }) => {
  const theme = useGetTheme();
  const [studentView, setStudentView] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  const setCurrentTaskData = (taskId) => {
    const assignment = courseAssignments.find(
      (assignment) => assignment.id === taskId
    );
    setSelectedAssignment(assignment);
  };

  useEffect(() => {
    if (!selectedAssignment) {
      const firstAssignmentId = findLowestAssignmentId(courseAssignments);

      setCurrentTaskData(firstAssignmentId);
    }
  }, [courseAssignments]);

  const transformLegendText = (key, percentage) => {
    return LEGEND_MARKER_FOR_COACH_USAGE[key.toUpperCase()]
      ? `${LEGEND_MARKER_FOR_COACH_USAGE[key.toUpperCase()]} ${percentage}%`
      : null;
  };

  const assignemntRawData = useMemo(() => {
    return data?.[selectedAssignment?.id];
  }, [selectedAssignment, data]);

  const graphKeys = useMemo(() => {
    if (studentView) {
      return ["chatPercentage", "total"];
    } else {
      return ["empty", "partial", "full"];
    }
  }, [data, studentView]);

  const graphData = useMemo(() => {
    if (!data) {
      return data;
    }
    let modifiedData = data;
    if (studentView) {
      modifiedData = Object.entries(data).map(([id, data]) => {
        return {
          id,
          total: (100 * data.submissionsStarted) / (data.submissionsTotal || 1)
        };
      });
    } else {
      modifiedData = Object.entries(data).map(([id, data]) => {
        let empty = data.total - data.started;
        return {
          id,
          total: data.total ? 100 : 0,
          empty: (100 * empty) / (data.total || 1),
          partial: (100 * (data.started - data.completed)) / (data.total || 1),
          full: (100 * data.completed) / (data.total || 1)
        };
      });
    }
    console.log("modifiedData", modifiedData);
    // sort by task due data
    const taskDueDatesMap = courseAssignments.reduce((acc, task) => {
      acc[task.id] = new Date(task.original_due_date);
      return acc;
    }, {});

    // Sort chart data based on due dates
    const sortedChartData = [...modifiedData].sort((a, b) => {
      const dateA = taskDueDatesMap[a.id];
      const dateB = taskDueDatesMap[b.id];
      return dateA - dateB;
    });

    return sortedChartData;
  }, [data, studentView]);

  // Build Legend Data

  const legendData = useMemo(() => {
    if (isEmpty(graphData) || !selectedAssignment?.id) return;
    let assignmentData = {};
    const { ...partialData } =
      graphData.find((a) => Number(a.id) === selectedAssignment.id) || {};
    delete partialData["studentsEngPercentage"];
    assignmentData = partialData;

    const chartData = Object.entries(flattenObject(assignmentData))
      .map((entry) => ({
        cat: entry[0],
        val: entry[1]
      }))
      .filter(
        (entry) =>
          entry.val >= 0 &&
          !isNaN(entry.val) &&
          !["studentsEngPercentage", "total", "id"].includes(entry.cat)
      );
    const divisor = chartData.reduce((sum, item) => sum + item.val, 0);
    const chartDataPercentages = adjustPercentages(chartData, divisor);
    return chartDataPercentages;
  }, [selectedAssignment, graphData, data]);

  const renderHeader = () => (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "60px",
        flexFlow: "row nowrap",
        alignItems: "center",
        borderBottom: `1px solid ${theme.palette.border.secondary}`
      }}>
      <Typography variant="subtitle1">AI Academic Coach Usage</Typography>
      <RichTooltipPopover
        name="Average time spent on assignments"
        body={tooltip}
      />
    </Box>
  );

  const renderViewToggle = () => (
    <Box sx={{ paddingTop: "16px" }}>
      <CustomIconButton
        icon={InsertChartIcon}
        label="Student view"
        isActive={studentView}
        onClick={() => setStudentView(true)}
      />
      <CustomIconButton
        icon={InsertChartIcon}
        label="Chat view"
        isActive={!studentView}
        onClick={() => setStudentView(false)}
      />
    </Box>
  );

  const renderChart = () => (
    <Box sx={{ height: "80%" }}>
      <PercentageGraph
        data={graphData}
        keys={graphKeys}
        selectedAssignment={selectedAssignment}
        transformLegendText={transformLegendText}
        clickOnAssignent={setCurrentTaskData}
        legendData={legendData}
        renderBarLabel={renderBarLabel}
        selectedAssignmentData={assignemntRawData}
      />
    </Box>
  );

  const averageSummary = useMemo(() => {
    let assignments = courseAssignments.filter((assignment) => {
      const dueDate = new Date(assignment.original_due_date);
      return dueDate <= new Date() && assignment.status === "Submitted";
    });
    if (!assignments.length) {
      return "No Data yet";
    }

    const summary = assignments.reduce(
      (acc, assignment) => {
        const taskId = assignment.id;
        const taskData = data[String(taskId)];
        if (taskData) {
          if (studentView) {
            acc.completed +=
              taskData.submissionsCompleted /
              (taskData.submissionsStarted || 1);
            acc.total += taskData.submissionsTotal ? 1 : 0;
            acc.totalWithChats += taskData.submissionsStarted ? 1 : 0;
            acc.started +=
              taskData.submissionsStarted / (taskData.submissionsTotal || 1);
          } else {
            acc.completed += taskData.completed / (taskData.started || 1);
            acc.total += taskData.total ? 1 : 0;
            acc.totalWithChats += taskData.started ? 1 : 0;
            acc.started += taskData.started / (taskData.total || 1);
          }
        }
        return acc;
      },
      { started: 0, completed: 0, total: 0, totalWithChats: 0 }
    );
    const used = summary.started / (summary.total || 1);
    const completed = summary.completed / (summary.totalWithChats || 1);
    const detail = studentView
      ? " (for past Assignments with Submissions) "
      : " ( for past interactions in assignments with submissions) ";
    return `Started Chat ${detail} : %${(100 * used).toFixed(2)} , Completed Chat (Out Of Started): %${(100 * completed).toFixed(2)}`;
  }, [data, courseAssignments, studentView]);

  return (
    <Box
      sx={{
        paddingInline: "24px",
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.border.secondary}`,
        height: "550px",
        borderRadius: "5px"
      }}>
      {renderHeader()}
      <Box
        sx={{
          display: "flex",
          flexFlow: "column nowrap",
          width: "inherit",
          height: "100%"
        }}>
        {renderViewToggle()}
        <Box>{renderChart()}</Box>
        <Box>
          <Typography variant="title2" sx={{ paddingTop: "16px" }}>
            Average
          </Typography>
          <Typography variant="subtitle2">{averageSummary}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatReport;
